import { Box, Collapse, Link, Stack, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { AxiosInstance } from "axios";
import { useEffect, useState } from "react";
import Panel from "../components/Panel";
import StatTile from "../components/StatTile";
import Timeline, { buildSegments, fmtDuration } from "../components/Timeline";
import { relativeTime } from "../components/format";
import { Run, RunsResponse } from "../types";
import { fromUnix } from "helpers/dateFormat";

interface AvailabilityPanelProps {
    req: AxiosInstance;
    id: string;
}

// AvailabilityPanel shows a node's run/restart history: a summary, an up/down
// timeline, and an expandable per-run table.
export default function AvailabilityPanel({ req, id }: AvailabilityPanelProps) {
    const [runs, setRuns] = useState<Run[] | null>(null);
    const [error, setError] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        let active = true;
        req.get<RunsResponse>(`/api/services/${encodeURIComponent(id)}/runs`)
            .then((resp) => { if (active) { setRuns(resp.data.runs || []); setError(false); } })
            .catch(() => { if (active) setError(true); });
        return () => { active = false; };
    }, [req, id]);

    if (error) {
        return (
            <Panel title="Uptime & restarts">
                <Box sx={{ color: "error.main", fontSize: 14 }}>Could not load run history.</Box>
            </Panel>
        );
    }

    if (!runs) {
        return (
            <Panel title="Uptime & restarts">
                <Box sx={{ color: "text.secondary", fontSize: 14 }}>Loading…</Box>
            </Panel>
        );
    }

    if (runs.length === 0) {
        return (
            <Panel title="Uptime & restarts">
                <Box sx={{ color: "text.secondary", fontSize: 14 }}>No run history yet.</Box>
            </Panel>
        );
    }

    const sorted = [...runs].sort((a, b) => a.startedAt - b.startedAt);
    const now = Math.floor(Date.now() / 1000);
    const last = sorted[sorted.length - 1];
    const running = now - last.lastSeenAt <= 90;
    const restarts = sorted.length - 1;
    const currentUptime = running ? now - last.startedAt : 0;
    const totalDowntime = buildSegments(sorted, now)
        .filter((s) => s.kind === "down")
        .reduce((acc, s) => acc + (s.end - s.start), 0);

    return (
        <Panel title="Uptime & restarts">
            <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
                <StatTile label="Restarts" value={restarts} hint={`${sorted.length} runs`} />
                <StatTile label="Current uptime" value={running ? fmtDuration(currentUptime) : "—"} hint={running ? "running" : "down"} />
                <StatTile label="Total downtime" value={fmtDuration(totalDowntime)} hint="observed window" />
                <StatTile label="Last restart" value={restarts > 0 ? relativeTime(last.startedAt) : "—"} />
            </Stack>

            <Timeline runs={sorted} />

            <Link component="button" underline="hover" sx={{ mt: 1.5, display: "inline-block" }} onClick={() => setOpen((v) => !v)}>
                {open ? "Hide" : "Show"} run details ({sorted.length})
            </Link>
            <Collapse in={open}>
                <Table size="small" sx={{ mt: 1 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Run</TableCell>
                            <TableCell>Started</TableCell>
                            <TableCell>Last seen</TableCell>
                            <TableCell align="right">Uptime</TableCell>
                            <TableCell align="right">Down before</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sorted.slice().reverse().map((r, idx) => {
                            const origIdx = sorted.length - 1 - idx;
                            const isLast = origIdx === sorted.length - 1;
                            const isRunning = isLast && running;
                            const upEnd = isRunning ? now : r.lastSeenAt;
                            const downBefore = origIdx > 0 ? r.startedAt - sorted[origIdx - 1].lastSeenAt : null;
                            return (
                                <TableRow key={r.id}>
                                    <TableCell sx={{ fontFamily: "monospace" }}>
                                        {r.id.slice(0, 8)}{isRunning ? " (current)" : ""}
                                    </TableCell>
                                    <TableCell>{fromUnix(r.startedAt).toLocaleString()}</TableCell>
                                    <TableCell>{isRunning ? "running" : fromUnix(r.lastSeenAt).toLocaleString()}</TableCell>
                                    <TableCell align="right">{fmtDuration(upEnd - r.startedAt)}</TableCell>
                                    <TableCell align="right">{downBefore === null ? "—" : fmtDuration(downBefore)}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Collapse>
        </Panel>
    );
}
